import { usePortalMyProfileUpdateMutation } from "@dashboard/graphql";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { ErrorText } from "./components";
import { portalLabels as l } from "./labels";
import { portalMessages as m } from "./messages";

export interface OwnProfile {
  phone: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  nif: string;
  niss: string;
  birthDate?: string | null;
  personalEmail: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
}

type Key = keyof OwnProfile;

// Field, label, input type, max length; required ones come from the API.
const FIELDS: Array<[Key, MessageDescriptor, "text" | "email" | "date", number]> = [
  ["phone", l.phone, "text", 16],
  ["streetAddress", l.address, "text", 256],
  ["postalCode", l.postalCode, "text", 8],
  ["city", l.city, "text", 128],
  ["emergencyContactName", l.emergencyContactName, "text", 256],
  ["emergencyContactPhone", l.emergencyContactPhone, "text", 16],
  ["nif", l.nif, "text", 9],
  ["niss", l.niss, "text", 11],
  ["birthDate", l.birthDate, "date", 10],
  ["personalEmail", l.personalEmailHint, "email", 254],
];

const API_NAME: Record<string, Key> = {
  phone: "phone",
  street_address: "streetAddress",
  postal_code: "postalCode",
  city: "city",
  emergency_contact_name: "emergencyContactName",
  emergency_contact_phone: "emergencyContactPhone",
};

export const ProfileForm = ({
  profile,
  missingFields,
  submitLabel,
  onSaved,
  onCancel,
}: {
  profile: OwnProfile;
  missingFields: string[];
  submitLabel: MessageDescriptor;
  onSaved: () => void;
  onCancel?: () => void;
}) => {
  const intl = useIntl();
  const required = new Set<Key>(
    [
      "phone",
      "street_address",
      "postal_code",
      "city",
      "emergency_contact_name",
      "emergency_contact_phone",
    ].map(name => API_NAME[name]),
  );
  const missing = new Set(missingFields.map(name => API_NAME[name]).filter(Boolean));
  const [values, setValues] = useState<OwnProfile>({
    ...profile,
    birthDate: profile.birthDate ?? "",
  });
  const [update, { data, loading }] = usePortalMyProfileUpdateMutation({
    onCompleted: result => {
      if (!result.myProfileUpdate?.errors.length) {
        onSaved();
      }
    },
  });
  const errors = data?.myProfileUpdate?.errors ?? [];
  const errorOf = (key: Key) => errors.find(error => error.field === key)?.message;
  const incomplete = [...required].some(key => !String(values[key] ?? "").trim());

  return (
    <Box
      as="form"
      display="flex"
      flexDirection="column"
      gap={3}
      onSubmit={event => {
        event.preventDefault();
        update({
          variables: {
            input: Object.fromEntries(
              FIELDS.map(([key]) => [
                key,
                key === "birthDate" ? values.birthDate || null : String(values[key] ?? "").trim(),
              ]),
            ),
          },
        });
      }}
    >
      <Box display="grid" gap={3} __gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))">
        {FIELDS.map(([key, label, type, maxLength]) => (
          <Box key={key} display="flex" flexDirection="column" gap={1}>
            <Input
              label={`${intl.formatMessage(label)}${required.has(key) ? " *" : ""}`}
              type={type}
              value={String(values[key] ?? "")}
              maxLength={maxLength}
              error={!!errorOf(key) || (missing.has(key) && !String(values[key] ?? "").trim())}
              onChange={event => setValues({ ...values, [key]: event.target.value })}
            />
            {errorOf(key) && (
              <Text size={1} color="critical1">
                {errorOf(key)}
              </Text>
            )}
          </Box>
        ))}
      </Box>
      <ErrorText errors={errors.filter(error => !error.field)} />
      <Box display="flex" gap={2} justifyContent="flex-end">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {intl.formatMessage(m.cancel)}
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={loading || incomplete}>
          {intl.formatMessage(submitLabel)}
        </Button>
      </Box>
    </Box>
  );
};
